// 首页 Hero 分栏背景图
export const ROW_BG_TYPES = {
  SOLID: 'solid',
  GRADIENT: 'gradient',
  SPLIT: 'split'
}

export const ROW_BG_SPLIT_DIRECTIONS = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical'
}

export const ROW_BG_SPLIT_COLOR_PRESETS = [
  '#d40000',
  '#1a1a2e',
  '#2563eb',
  '#16a34a',
  '#f59e0b',
  '#9333ea'
]

/** 从预设色板中选取下一个未使用的分栏背景色 */
export function pickNextSplitColor(existingColors = []) {
  const used = new Set(
    existingColors.map((color) => String(color || '').trim().toLowerCase()).filter(Boolean)
  )

  const available = ROW_BG_SPLIT_COLOR_PRESETS.find(
    (color) => !used.has(color.toLowerCase())
  )
  if (available) return available

  return ROW_BG_SPLIT_COLOR_PRESETS[existingColors.length % ROW_BG_SPLIT_COLOR_PRESETS.length]
}

/** 创建默认的行背景样式对象 */
export function createDefaultRowBackgroundStyle() {
  return {
    type: ROW_BG_TYPES.SOLID,
    color: '',
    angle: 90,
    colors: ['#d40000', '#1a1a2e'],
    direction: ROW_BG_SPLIT_DIRECTIONS.HORIZONTAL
  }
}

/** 判断 CSS 值是否为渐变背景 */
export function isCssGradient(value) {
  return /gradient\s*\(/i.test(String(value || ''))
}

/** 确保颜色数组至少包含两个有效色值 */
function normalizeColors(colors, fallback = ['#d40000', '#1a1a2e']) {
  const list = Array.isArray(colors) ? colors.map((c) => String(c || '').trim()).filter(Boolean) : []
  return list.length >= 2 ? list : [...fallback]
}

/** 解析 linear-gradient 中的颜色停靠点 */
function parseGradientStops(stopsStr) {
  const stops = []
  const pattern = /(#[0-9a-f]{3,8}|rgba?\(\s*[^)]+\)|[a-z]+)\s+([0-9.]+)%/gi
  let match = pattern.exec(stopsStr)

  while (match) {
    stops.push({
      color: match[1].trim(),
      percent: Number(match[2])
    })
    match = pattern.exec(stopsStr)
  }

  return stops
}

/** 从渐变停靠点提取不重复的颜色列表 */
function extractSplitColors(stops) {
  const colors = []

  stops.forEach((stop) => {
    const color = stop.color
    const last = colors[colors.length - 1]
    if (!last || String(last).toLowerCase() !== String(color).toLowerCase()) {
      colors.push(color)
    }
  })

  return colors
}

/** 将 CSS 背景字符串解析为结构化背景样式对象 */
export function parseBackgroundStyleCss(css) {
  const defaults = createDefaultRowBackgroundStyle()
  const value = String(css || '').trim()
  if (!value) return defaults

  if (!isCssGradient(value)) {
    return {
      ...defaults,
      color: value
    }
  }

  const inner = value.replace(/^linear-gradient\s*\(/i, '').replace(/\)\s*$/, '').trim()
  const splitMatch = inner.match(/^to\s+(right|bottom)\s*,\s*(.+)$/i)

  if (splitMatch) {
    const stops = parseGradientStops(splitMatch[2])
    const colors = extractSplitColors(stops)

    if (colors.length >= 2) {
      return {
        ...defaults,
        type: ROW_BG_TYPES.SPLIT,
        direction:
          splitMatch[1].toLowerCase() === 'bottom'
            ? ROW_BG_SPLIT_DIRECTIONS.VERTICAL
            : ROW_BG_SPLIT_DIRECTIONS.HORIZONTAL,
        colors: normalizeColors(colors)
      }
    }
  }

  const angleMatch = inner.match(/^(-?\d+(?:\.\d+)?)deg\s*,\s*(.+)$/i)
  if (angleMatch) {
    const stops = parseGradientStops(angleMatch[2])
    const colors = stops.map((stop) => stop.color).filter(Boolean)

    if (colors.length >= 2) {
      return {
        ...defaults,
        type: ROW_BG_TYPES.GRADIENT,
        angle: Number(angleMatch[1]),
        colors: normalizeColors(colors)
      }
    }
  }

  return {
    ...defaults,
    type: ROW_BG_TYPES.GRADIENT,
    colors: ['#d40000', '#1a1a2e']
  }
}

/** 标准化行背景样式（支持字符串、对象及 legacy backgroundColor） */
export function normalizeRowBackgroundStyle(row) {
  const defaults = createDefaultRowBackgroundStyle()
  const raw = row?.backgroundStyle

  if (typeof raw === 'string') {
    const css = raw.trim()
    if (css) {
      return parseBackgroundStyleCss(css)
    }
  }

  if (raw?.type) {
    const style = {
      ...defaults,
      ...raw,
      type: Object.values(ROW_BG_TYPES).includes(raw.type) ? raw.type : ROW_BG_TYPES.SOLID,
      angle: Number.isFinite(Number(raw.angle)) ? Number(raw.angle) : defaults.angle,
      direction:
        raw.direction === ROW_BG_SPLIT_DIRECTIONS.VERTICAL
          ? ROW_BG_SPLIT_DIRECTIONS.VERTICAL
          : ROW_BG_SPLIT_DIRECTIONS.HORIZONTAL
    }

    if (style.type === ROW_BG_TYPES.SOLID) {
      style.color = String(raw.color ?? row?.backgroundColor ?? '').trim()
      if (isCssGradient(style.color)) {
        style.color = ''
      }
    } else {
      style.colors = normalizeColors(raw.colors, defaults.colors)
    }

    return style
  }

  const legacy = String(row?.backgroundColor || '').trim()
  if (legacy && isCssGradient(legacy)) {
    return parseBackgroundStyleCss(legacy)
  }

  return {
    ...defaults,
    color: legacy
  }
}

/** 将结构化背景样式转为 CSS 背景字符串 */
export function buildRowBackgroundCss(style) {
  const normalized =
    style && typeof style === 'object' && style.type
      ? style
      : typeof style === 'string'
        ? parseBackgroundStyleCss(style)
        : normalizeRowBackgroundStyle({ backgroundStyle: style })

  if (normalized.type === ROW_BG_TYPES.GRADIENT) {
    return buildGradientCss(normalized)
  }

  if (normalized.type === ROW_BG_TYPES.SPLIT) {
    return buildSplitCss(normalized)
  }

  return normalized.color || '#ffffff'
}

/** 序列化行背景样式为 CSS 字符串（有背景图时可清空） */
export function serializeRowBackgroundStyle(row, options = {}) {
  const { clearWhenImage = true } = options
  if (!row || (clearWhenImage && rowHasBackgroundImage(row))) return ''

  const style = normalizeRowBackgroundStyle(row)
  if (style.type === ROW_BG_TYPES.SOLID && !style.color) {
    return ''
  }

  return buildRowBackgroundCss(style)
}

/** 构建角度渐变 CSS */
function buildGradientCss({ angle, colors }) {
  const stops = normalizeColors(colors)
  const list = stops
    .map((color, index) => {
      const percent = stops.length === 1 ? 0 : (index / (stops.length - 1)) * 100
      return `${color} ${percent}%`
    })
    .join(', ')

  return `linear-gradient(${angle}deg, ${list})`
}

/** 构建分栏色块渐变 CSS */
function buildSplitCss({ direction, colors }) {
  const stops = normalizeColors(colors)
  if (stops.length === 1) {
    return stops[0]
  }

  const segment = 100 / stops.length
  const parts = []

  stops.forEach((color, index) => {
    // start
    const start = (index * segment).toFixed(4)
    // end
    const end = ((index + 1) * segment).toFixed(4)
    parts.push(`${color} ${start}%`, `${color} ${end}%`)
  })

  const axis = direction === ROW_BG_SPLIT_DIRECTIONS.VERTICAL ? 'to bottom' : 'to right'
  return `linear-gradient(${axis}, ${parts.join(', ')})`
}

/** 判断行是否配置了背景图 */
export function rowHasBackgroundImage(row) {
  return !!(row?.backgroundUrl || row?.mediaUrl)
}

/** 清空行的背景样式与 backgroundColor */
export function clearRowBackground(row) {
  if (!row || typeof row !== 'object') return
  row.backgroundStyle = createDefaultRowBackgroundStyle()
  row.backgroundColor = ''
}

/** 解析 COLUMN 行的背景内联样式（有图时返回空对象） */
export function resolveColumnRowBackgroundStyle(row) {
  if (rowHasBackgroundImage(row)) {
    return {}
  }

  const css = buildRowBackgroundCss(normalizeRowBackgroundStyle(row))
  if (isCssGradient(css)) {
    return { background: css }
  }
  return { backgroundColor: css }
}

/** 解析 COLUMN 行的背景色（渐变为 transparent） */
export function resolveColumnRowBackgroundColor(row) {
  const css = buildRowBackgroundCss(normalizeRowBackgroundStyle(row))
  return isCssGradient(css) ? 'transparent' : css
}

/** 将首行背景样式上移到 COLUMN 条目级（兼容旧数据结构） */
export function migrateColumnBackgroundFromRows(item) {
  if (!item || typeof item !== 'object') return

  const current = item.backgroundStyle
  if (typeof current === 'string' ? current.trim() : current?.type) {
    return
  }

  const rows = item.rows || []
  for (const row of rows) {
    const rowStyle = row?.backgroundStyle
    const hasRowStyle = typeof rowStyle === 'string' ? rowStyle.trim() : rowStyle?.type
    if (hasRowStyle || row?.backgroundColor) {
      item.backgroundStyle = hasRowStyle ? rowStyle : row.backgroundColor
      delete row.backgroundStyle
      row.backgroundColor = ''
      return
    }
  }
}

/** 同步行的 backgroundStyle 与 backgroundColor 字段 */
export function syncRowBackgroundFields(row, options = {}) {
  const { clearWhenImage = true } = options
  if (!row || typeof row !== 'object') return

  if (clearWhenImage && rowHasBackgroundImage(row)) {
    clearRowBackground(row)
    return
  }

  row.backgroundStyle = normalizeRowBackgroundStyle(row)
  const { type, color } = row.backgroundStyle

  if (type === ROW_BG_TYPES.SOLID && !color) {
    row.backgroundColor = ''
    return
  }

  row.backgroundColor = buildRowBackgroundCss(row.backgroundStyle)
}

/** 判断 COLUMN 条目是否任一行含有背景图 */
export function columnHasAnyRowImage(column) {
  const rows = column?.rows
  if (!Array.isArray(rows)) return false
  return rows.some((row) => rowHasBackgroundImage(row))
}

/** 解析 COLUMN 条目级背景内联样式 */
export function resolveColumnBackgroundStyle(column) {
  const css = buildRowBackgroundCss(normalizeRowBackgroundStyle(column))
  if (!css || (css === '#ffffff' && !normalizeRowBackgroundStyle(column).color)) {
    return {}
  }
  if (isCssGradient(css)) {
    return { background: css }
  }
  return { backgroundColor: css }
}

/** 同步 COLUMN 条目级背景字段（不因背景图清空） */
export function syncColumnBackgroundFields(column) {
  syncRowBackgroundFields(column, { clearWhenImage: false })
}
